import React, { FC, useEffect } from "react";
import Modal from "@/components/Modal/index";
import { useAppTheme } from "@/utils/theme";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import { Controller, useForm } from "react-hook-form";
import Button from "@/components/Button";
import { OrganizationUnit } from "@/domain/organizations.interface";
import { OrganizationLabel } from "@/domain/organization-labels.interface";
import { OrganizationPayload } from "@/services/organizations.service";
import { useOrganizationLabels } from "@/hooks/useOrganizationLabels";

export type OrganizationFormData = {
  name: string;
  description: string;
  status: "ACTIVE" | "INACTIVE";
  orgUnitLabelId: string[];
};

type OrganizationModalProps = {
  open: boolean;
  onClose: () => void;
  organization?: OrganizationUnit;
  onSave: (data: OrganizationPayload) => void;
  isPending: boolean;
};

const OrganizationModal: FC<OrganizationModalProps> = ({
  open,
  onClose,
  organization,
  onSave,
  isPending,
}) => {
  const theme = useAppTheme();
  const isEdit = Boolean(organization);

  const { organizationLabels, isLoadingOrganizationLabels } =
    useOrganizationLabels({
      status: "ACTIVE",
      pageNumber: 0,
      pageSize: 500,
      sortField: "name",
      sortOrder: 1,
    });

  const availableLabels: OrganizationLabel[] = organizationLabels?.list || [];

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<OrganizationFormData>({
    mode: "all",
    defaultValues: {
      name: "",
      description: "",
      status: "ACTIVE",
      orgUnitLabelId: [],
    },
  });

  useEffect(() => {
    if (organization) {
      reset({
        name: organization.name || "",
        description: organization.description || "",
        status: (organization.status as "ACTIVE" | "INACTIVE") || "ACTIVE",
        orgUnitLabelId: Array.isArray(organization.orgUnitLabelId)
          ? organization.orgUnitLabelId
          : [],
      });
    } else {
      reset({
        name: "",
        description: "",
        status: "ACTIVE",
        orgUnitLabelId: [],
      });
    }
  }, [organization, reset]);

  const onSubmit = (data: OrganizationFormData) => {
    const payload: OrganizationPayload = {
      name: data.name.trim(),
      description: data.description.trim(),
      status: data.status,
      orgUnitLabelId: data.orgUnitLabelId,
    };
    onSave(payload);
  };

  const selectedIds = watch("orgUnitLabelId") || [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={isPending}
      theme={theme}
      title={isEdit ? "Edit Organization" : "Create Organization"}
      size={{
        width: "100%",
        minHeight: 460,
        maxHeight: 620,
        maxWidth: 560,
      }}
      content={
        <Box sx={{ width: "100%", padding: "20px 0" }}>
          <Grid container spacing={"20px"}>
            <Grid item xs={12}>
              <Controller
                name="name"
                control={control}
                rules={{
                  required: "Name is required",
                  maxLength: {
                    value: 100,
                    message: "Name must be 100 characters or less",
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Name"
                    required
                    size="medium"
                    error={!!errors.name}
                    helperText={errors.name?.message || ""}
                    disabled={isPending}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                rules={{
                  required: "Description is required",
                  maxLength: {
                    value: 500,
                    message: "Description must be 500 characters or less",
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Description"
                    required
                    multiline
                    minRows={3}
                    maxRows={5}
                    error={!!errors.description}
                    helperText={errors.description?.message || ""}
                    disabled={isPending}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="status"
                control={control}
                rules={{ required: "Status is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    label="Status"
                    required
                    error={!!errors.status}
                    helperText={errors.status?.message || ""}
                    disabled={isPending}
                  >
                    <MenuItem value="ACTIVE">Active</MenuItem>
                    <MenuItem value="INACTIVE">Inactive</MenuItem>
                  </TextField>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={availableLabels}
                loading={isLoadingOrganizationLabels}
                disabled={isPending}
                getOptionLabel={(option) => option.name || option._id || ""}
                isOptionEqualToValue={(option, value) =>
                  option._id === value._id
                }
                value={availableLabels.filter((l) =>
                  selectedIds.includes(l._id)
                )}
                onChange={(_e, selected) => {
                  setValue(
                    "orgUnitLabelId",
                    selected.map((l) => l._id),
                    { shouldDirty: true }
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Organization Labels"
                    placeholder="Select labels"
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
      }
      actions={
        <Box
          sx={{
            display: "flex",
            gap: "10px",
          }}
        >
          <Button
            variantType="cancel"
            onClick={onClose}
            disabled={isPending}
            label="CANCEL"
          />
          <Button
            variantType="save"
            onClick={() => handleSubmit(onSubmit)()}
            disabled={isPending}
            isLoading={isPending}
            label="SAVE"
          />
        </Box>
      }
    />
  );
};

export default OrganizationModal;
